import { GetConfigureFormOperations, Operation } from '@occc-app/offerHelpers/types';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getLimitsInConstraintOperations, LimitType } from '@occc/feat-limits';
import { getFulfillmentWindowOperations } from '@occc/feat-constraints';
import {
  makeMarketingContentCreateOperation,
  makeMarketingContentUpdateOperation,
} from '@occc-app/offerHelpers/operations/marketingContent';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { Offer_Presentation_Type, OfferFragment } from '@occc/graphql';

import { getStoredValueEarningMechanismOperations } from './stored-value-earning-mechanism-operations';
import { StoredValueConfigureFormValues } from './stored-value-configure-form.types';
import { getStoredValueProgramOperations } from './stored-value-program-operations';

const getStoredValueMarketingContentOperation = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: StoredValueConfigureFormValues,
  dirtyFields: Parameters<GetConfigureFormOperations<StoredValueConfigureFormValues>>[3],
): Operation[] => {
  // promotionTitle (used in digital receipts) is derived from marketingContents.title on the backend;
  // stored value offers have no Design tab, so we sync the title manually from the program name.
  const existingMarketingContent = offer?.marketingContents?.[0];

  if (!existingMarketingContent) {
    return [makeMarketingContentCreateOperation({ offerId, title: values.name })];
  }

  if (isFieldDirty<StoredValueConfigureFormValues>(dirtyFields, 'name')) {
    return [
      makeMarketingContentUpdateOperation({ id: existingMarketingContent.id, title: values.name }),
    ];
  }

  return [];
};

export const getStoredValueConfigureFormOperations: GetConfigureFormOperations<
  StoredValueConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getStoredValueEarningMechanismOperations(
    offerId,
    values,
    dirtyFields,
  );
  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
  );
  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );
  const nameAndDescriptionOperations = getOfferNameAndDescriptionUpdateOperations(
    offerId,
    values,
    dirtyFields,
  );
  const marketingContentOperations = getStoredValueMarketingContentOperation(
    offerId,
    offer,
    values,
    dirtyFields,
  );
  const storedValueProgramOperations = getStoredValueProgramOperations(
    offerId,
    values,
    dirtyFields,
  );

  const additionalOperations: Operation[] = [];
  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }
  if (!offer || offer.presentationType !== Offer_Presentation_Type.Hidden) {
    additionalOperations.push(
      makeOfferUpdateOperation(offerId, {
        presentationType: Offer_Presentation_Type.Hidden, // always hide the Stored Value offers
      }),
    );
  }

  return [
    ...earningMechanismOperations,
    ...fulfillmentWindowOperations,
    ...awardCountLimitsOperations,
    ...nameAndDescriptionOperations,
    ...marketingContentOperations,
    ...additionalOperations,
    ...storedValueProgramOperations,
  ];
};
